import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useFetchApi } from './useFetchApi';
import { useNotification } from './useNotification';
import { getPropertiesDiff } from '../helper/data-transform';
import { User } from '../shared/types';

export type PlotRiskAssessmentStatus =
  | 'NEW'
  | 'UNDER_REVIEW'
  | 'IN_RISK_MITIGATION'
  | 'EUDR_COMPLIANT'
  | 'EUDR_NON_COMPLIANT';

export type PlotRiskAssessmentResponse = {
  id: string;
  plotId: string;
  businessId: string;
  updatedAt: string;
  status: PlotRiskAssessmentStatus;
  notes: string;
};

export type PlotRiskAssessmentHistory = {
  entityId: string;
  action: 'CREATE' | 'UPDATE' | 'DELETE';
  afterData: PlotRiskAssessmentResponse;
  beforeData: PlotRiskAssessmentResponse;
  changedAt: string;
  changedByUser: User;
  changedByUserType: 'USER' | 'SYSTEM' | 'M2M';
  status?: PlotRiskAssessmentStatus;
  notes?: string;
  updatedAt: string;
  updatedBy?: string;
};

const PLOT_RISK_ASSESSMENTS_QUERY = 'plotRiskAssessments';
export const useGetPlotRiskAssessments = () => {
  const fetch = useFetchApi();

  return useQuery({
    queryKey: [PLOT_RISK_ASSESSMENTS_QUERY],
    queryFn: () => fetch('plot-risk-assessments'),
    refetchOnWindowFocus: false,
  });
};

const PLOT_RISK_ASSESSMENT_HISTORY_QUERY = 'plotRiskAssessmentHistory';
export const useGetPlotRiskAssessmentHistory = (
  assessmentId: string | undefined
) => {
  const fetch = useFetchApi();
  return useQuery({
    queryKey: [PLOT_RISK_ASSESSMENT_HISTORY_QUERY, assessmentId],
    queryFn: () => fetch(`plot-risk-assessments/${assessmentId}/history`),
    enabled: !!assessmentId,
    select: (data) => {
      return data.map((record: PlotRiskAssessmentHistory) => ({
        ...record,
        ...getPropertiesDiff({
          before: record.beforeData,
          after: record.afterData,
        }),
        updatedBy: [
          record.changedByUser.firstName,
          record.changedByUser.lastName,
        ].join(' '),
      }));
    },
  });
};

const usePlotRiskAssessment = () => {
  const fetch = useFetchApi();
  const queryClient = useQueryClient();
  const { showErrorWithResponse, successfullyUpdated } = useNotification();

  const onSuccess = (data: PlotRiskAssessmentResponse) => {
    queryClient.invalidateQueries({
      queryKey: [PLOT_RISK_ASSESSMENTS_QUERY],
    });
    queryClient.invalidateQueries({
      queryKey: [PLOT_RISK_ASSESSMENT_HISTORY_QUERY, data.id],
    });
    successfullyUpdated('Plot risk status');
  };

  const createAssessment = useMutation({
    mutationFn: (data: Partial<PlotRiskAssessmentResponse>) =>
      fetch(`plots/${data.plotId}/plot-risk-assessments`, {
        method: 'POST',
        body: JSON.stringify({
          status: data.status,
          notes: data.notes,
        }),
      }),
    onSuccess,
    onError: showErrorWithResponse,
  });

  const updateAssessment = useMutation({
    mutationFn: (data: Partial<PlotRiskAssessmentResponse>) =>
      fetch(`plot-risk-assessments/${data.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          status: data.status,
          notes: data.notes,
        }),
      }),
    onSuccess,
    onError: showErrorWithResponse,
  });

  return { createAssessment, updateAssessment };
};

export default usePlotRiskAssessment;
